// src/ConveInviteModal.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { UserSearchResult, GroupMember } from './types/groupChat';
import { groupChatApi } from './api/groupChatApi';

interface Props {
  roomId: number;
  existingMembers: GroupMember[];
  onClose: () => void;
  onInvited: () => void;
}

const ConveInviteModal: React.FC<Props> = ({ roomId, existingMembers, onClose, onInvited }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<UserSearchResult[]>([]);
  const [inviting, setInviting] = useState(false);
  const [searching, setSearching] = useState(false);

  const existingMemberIds = new Set(existingMembers.map((m) => m.userId));

  useEffect(() => {
    if (searchQuery.trim().length < 1) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        setSearching(true);
        const results = await groupChatApi.searchUsers(searchQuery.trim());
        // 이미 멤버인 사용자 제외
        setSearchResults(results.filter((u) => !existingMemberIds.has(u.id)));
      } catch {
        // alert는 api에서 처리
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const toggleUser = useCallback((user: UserSearchResult) => {
    setSelectedUsers((prev) => {
      const exists = prev.some((u) => u.id === user.id);
      return exists ? prev.filter((u) => u.id !== user.id) : [...prev, user];
    });
  }, []);

  const handleInvite = async () => {
    if (selectedUsers.length === 0) {
      alert('초대할 멤버를 선택해주세요.');
      return;
    }
    try {
      setInviting(true);
      await groupChatApi.inviteMembers(roomId, selectedUsers.map((u) => u.id));
      onInvited();
    } catch {
      // alert는 api에서 처리
    } finally {
      setInviting(false);
    }
  };

  return (
    <div className="conve-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="conve-modal">
        <div className="conve-modal-header">
          <h3>멤버 초대</h3>
          <button className="conve-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="conve-modal-body">
          {selectedUsers.length > 0 && (
            <div className="conve-selected-users">
              {selectedUsers.map((u) => (
                <span key={u.id} className="conve-selected-chip">
                  {u.name}
                  <button onClick={() => toggleUser(u)}>✕</button>
                </span>
              ))}
            </div>
          )}

          <input
            className="conve-modal-input"
            type="text"
            placeholder="이름 또는 이메일 검색..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <div className="conve-search-results">
            {searching && <div className="conve-searching">검색 중...</div>}
            {!searching && searchResults.length === 0 && searchQuery.trim().length > 0 && (
              <div className="conve-no-results">검색 결과가 없습니다</div>
            )}
            {searchResults.map((user) => {
              const isSelected = selectedUsers.some((u) => u.id === user.id);
              return (
                <div
                  key={user.id}
                  className={`conve-search-user-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => toggleUser(user)}
                >
                  <div className="conve-search-user-avatar">
                    {user.name[0]?.toUpperCase()}
                  </div>
                  <div className="conve-search-user-info">
                    <div className="conve-search-user-name">{user.name}</div>
                    <div className="conve-search-user-email">{user.email}</div>
                  </div>
                  {isSelected && <span className="conve-check">✓</span>}
                </div>
              );
            })}
          </div>
        </div>

        <div className="conve-modal-footer">
          <button className="conve-modal-cancel" onClick={onClose}>취소</button>
          <button
            className="conve-modal-confirm"
            onClick={handleInvite}
            disabled={inviting || selectedUsers.length === 0}
          >
            {inviting ? '초대 중...' : '초대하기'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConveInviteModal;
